import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Gift } from "lucide-react";
import { track } from "../../lib/analytics";
import { peekPendingReferralCode } from "../../lib/referralCapture";
import { useStore } from "../../stores/rootStore";

/**
 * Tells a visitor who arrived on an invite link that the code was recognized, and points them at
 * the only place it can be redeemed — signing up. Without this the capture is invisible: the code
 * sits in localStorage and the landing page looks identical to a cold visit (MUHAHA-407).
 *
 * Read once on mount: the code is captured in main.tsx before the first render, and the only thing
 * that edits it afterwards is the AuthForm field, which is already showing the same code.
 */
export const InviteBanner = observer(() => {
	const { auth, ui } = useStore();
	const { t } = useTranslation();
	const [code, setCode] = useState<string | null>(null);
	useEffect(() => setCode(peekPendingReferralCode()), []);
	const [dismissed, setDismissed] = useState(false);

	// Waits for bootstrap so a returning signed-in visitor never sees it flash.
	const visible = Boolean(code) && auth.initialized && !auth.isLoggedIn && !dismissed;

	useEffect(() => {
		if (visible) track("referral_banner_shown", {});
	}, [visible]);

	if (!visible) return null;

	return (
		<div className="flex items-center gap-2 border-b border-line bg-brand/10 px-4 py-2">
			<Gift size={16} className="shrink-0 text-brand" />
			<p className="min-w-0 flex-1 text-xs leading-relaxed">
				{t("auth.inviteBannerText")} <b className="tracking-wider">{code}</b>
			</p>
			<button
				type="button"
				onClick={() => ui.openAuthWithMode("signUp")}
				className="btn-primary shrink-0 px-3 py-1.5 text-xs"
			>
				{t("auth.inviteBannerCta")}
			</button>
			<button
				type="button"
				aria-label={t("common.close")}
				onClick={() => setDismissed(true)}
				className="grid size-7 shrink-0 place-items-center rounded-full text-lg leading-none text-faint transition hover:bg-surface-2 hover:text-text"
			>
				×
			</button>
		</div>
	);
});
